import React from 'react';
import {useIntl} from 'react-intl';
import {ComboBox, Text} from '@forecast-it/design-system';
import {InvoiceContact} from '../useInvoiceClientContacts';

const EMPTY_STATE_KEY = '__no_contacts__';

interface ManualContactComboBoxProps {
	contacts: InvoiceContact[];
	loading: boolean;
	value: InvoiceContact | null;
	onChange: (contact: InvoiceContact | null) => void;
	dataCy?: string;
}

export const ManualContactComboBox = ({contacts, loading, value, onChange, dataCy}: ManualContactComboBoxProps) => {
	const {formatMessage} = useIntl();

	const isEmpty = !loading && contacts.length === 0;
	const items = isEmpty
		? [{id: EMPTY_STATE_KEY, label: formatMessage({id: 'clients.client-contacts-empty-state'})}]
		: contacts.map(contact => ({id: contact.id, label: contact.name}));

	const handleChange = (key: string | null) => {
		if (loading) {
			return;
		}
		if (!key || key === EMPTY_STATE_KEY) {
			onChange(null);
			return;
		}
		onChange(contacts.find(contact => contact.id === key) ?? null);
	};

	return (
		<ComboBox
			data-cy={dataCy}
			label={formatMessage({id: 'common.contact'})}
			placeholder={formatMessage({id: loading ? 'common.loading' : 'project_settings.select_contact'})}
			disabled={loading}
			defaultItems={items}
			disabledKeys={isEmpty ? [EMPTY_STATE_KEY] : undefined}
			value={value?.id ?? null}
			onChange={handleChange}
			menuTrigger="focus"
		>
			{item => (
				<ComboBox.Item textValue={item.label}>
					<Text>{item.label}</Text>
				</ComboBox.Item>
			)}
		</ComboBox>
	);
};
